// Gantt panel with swimlanes, milestone diamonds, dependency hairlines, today line.
// lanes: [{ id, label, items: [{ id, name, startDate, endDate, progress?: 0..1, tone?, milestones?: [{ id, date, label }] }] }]

import { useOptionalRichTooltipController } from '../richTooltips/RichTooltipProvider.jsx';

const DAY_MS = 24 * 60 * 60 * 1000;
const MIN_BAR_WIDTH_PCT = 2;
const GANTT_ROW_HEIGHT = 44;
const GANTT_TRACK_CENTER_Y = 22;

export function GanttPanel({
  lanes = [],
  dependencies = [],
  startDate,
  endDate,
  today = new Date().toISOString().slice(0, 10),
  onSelect,
  selectedId,
  onSelectDependency,
  selectedDependencyId,
  height = 360,
}) {
  const tooltip = useOptionalRichTooltipController();
  if (!lanes.length) return null;
  const allItems = lanes.flatMap((l) => l.items);
  const explicitStartTs = parseDateOnly(startDate);
  const explicitEndTs = parseDateOnly(endDate);
  const fallbackDatePoints = [
    today,
    ...allItems.flatMap((i) => [
      i.startDate,
      i.endDate,
      ...(i.milestones || []).map((m) => m.date),
    ]),
  ]
    .map(parseDateOnly)
    .filter((n) => !Number.isNaN(n));
  const fallbackTodayTs = parseDateOnly(today);
  const rawMinTs = !Number.isNaN(explicitStartTs)
    ? explicitStartTs
    : fallbackDatePoints.length
      ? Math.min(...fallbackDatePoints)
      : fallbackTodayTs;
  const rawMaxTs = !Number.isNaN(explicitEndTs)
    ? explicitEndTs
    : fallbackDatePoints.length
      ? Math.max(...fallbackDatePoints)
      : fallbackTodayTs;
  const minTs = monthStart(rawMinTs);
  const maxTs = nextMonthStart(rawMaxTs);
  const span = Math.max(1, maxTs - minTs);
  const pct = (iso) => {
    const ts = parseDateOnly(iso);
    if (Number.isNaN(ts)) return 0;
    return Math.max(0, Math.min(100, ((ts - minTs) / span) * 100));
  };
  const barPosition = (startIso, endIso) => {
    const start = pct(startIso);
    const end = pct(endIso);
    const center = (start + end) / 2;
    const rawWidth = Math.max(0, end - start);
    const width = Math.max(MIN_BAR_WIDTH_PCT, rawWidth);
    const left =
      rawWidth < MIN_BAR_WIDTH_PCT ? Math.max(0, Math.min(100 - width, center - width / 2)) : start;
    return { left, width };
  };
  const markers = monthMarkers(minTs, maxTs).map((m, index, arr) => {
    const nextTs = arr[index + 1]?.ts ?? maxTs;
    return {
      ...m,
      left: pct(m.iso),
      width: Math.max(0, ((nextTs - m.ts) / span) * 100),
    };
  });
  const itemCount = allItems.length;
  const laneCount = lanes.length;
  const todayLineHeight = Math.max(42, Math.min(height, 42 + 24 + laneCount * 32 + itemCount * 44));

  return (
    <div
      className="gantt-panel"
      style={{ maxHeight: height, '--gantt-today-line-height': `${todayLineHeight}px` }}
    >
      <header className="gantt-header">
        <span className="gantt-row-label">Initiative</span>
        <div className="gantt-timeline">
          {markers.map((m) => (
            <div
              key={m.iso}
              className="gantt-tick"
              style={{ left: `${m.left}%`, width: `${m.width}%` }}
              data-testid={`gantt-tick-${m.iso}`}
            >
              {m.label}
            </div>
          ))}
          <div
            className="gantt-today-line"
            style={{ left: `${pct(today)}%` }}
            data-testid="gantt-today-line"
            aria-hidden
          >
            <span className="gantt-today-cap" />
          </div>
        </div>
      </header>
      <div className="gantt-body">
        {lanes.map((lane) => (
          <section key={lane.id} className="gantt-swimlane">
            <h4 className="gantt-swimlane-label">{lane.label}</h4>
            <div className="gantt-lane-rows">
              {(() => {
                const itemPositions = lane.items.map((it) => {
                  const position = barPosition(it.startDate, it.endDate);
                  const baselinePosition =
                    it.baselineStartDate || it.baselineEndDate
                      ? barPosition(
                          it.baselineStartDate || it.startDate,
                          it.baselineEndDate || it.endDate,
                        )
                      : null;
                  return {
                    item: it,
                    position,
                    baselinePosition,
                  };
                });
                const itemIds = new Set(itemPositions.map(({ item }) => item.id));
                const laneDependencies = dependencies.filter(
                  (dependency) =>
                    itemIds.has(dependency.predecessorId) && itemIds.has(dependency.successorId),
                );
                const dependencyRowsHeight = itemPositions.length * GANTT_ROW_HEIGHT;
                const yForIndex = (index) => index * GANTT_ROW_HEIGHT + GANTT_TRACK_CENTER_Y;
                return (
                  <>
                    {itemPositions.map(({ item: it, position, baselinePosition }) => {
                      const { left, width } = position;
                      const tone = it.tone || 'blue';
                      const isSelected = selectedId === it.id;
                      const active = Boolean(
                        it.richTooltip?.surfaceId && tooltip?.isActive(it.richTooltip.surfaceId),
                      );
                      const pinned = Boolean(
                        it.richTooltip?.surfaceId && tooltip?.isPinned(it.richTooltip.surfaceId),
                      );
                      const barTriggerProps =
                        it.richTooltip && tooltip
                          ? tooltip.getTriggerProps({
                              surfaceId: it.richTooltip.surfaceId,
                              payload: it.richTooltip,
                              testIdPrefix: 'rich-tooltip',
                              ariaLabel: `${it.name} schedule details`,
                              onClick: () => onSelect && onSelect(it.__raw || it),
                            })
                          : {};
                      return (
                        <div key={it.id} className={`gantt-row ${isSelected ? 'is-selected' : ''}`}>
                          <div className="gantt-row-label-wrap">
                            <button
                              className="gantt-row-label"
                              type="button"
                              onClick={() => onSelect && onSelect(it.__raw || it)}
                            >
                              {it.name}
                            </button>
                            {it.criticalPath ? (
                              <span
                                className="gantt-critical-flag"
                                data-testid={`gantt-critical-${it.id}`}
                              >
                                Critical path
                              </span>
                            ) : null}
                          </div>
                          <div className="gantt-track">
                            {baselinePosition ? (
                              <span
                                className="gantt-baseline"
                                style={{
                                  left: `${baselinePosition.left}%`,
                                  width: `${baselinePosition.width}%`,
                                }}
                                data-testid={`gantt-baseline-${it.id}`}
                                aria-hidden
                              />
                            ) : null}
                            <button
                              className={`gantt-bar tone-${tone}${active ? ' is-active' : ''}${pinned ? ' is-pinned' : ''}`}
                              style={{ left: `${left}%`, width: `${width}%` }}
                              type="button"
                              onClick={
                                !it.richTooltip
                                  ? () => onSelect && onSelect(it.__raw || it)
                                  : undefined
                              }
                              data-testid={`gantt-bar-${it.id}`}
                              aria-label={`${it.name} schedule`}
                              {...barTriggerProps}
                            >
                              {it.progress != null ? (
                                <span
                                  className="gantt-bar-progress"
                                  style={{ width: `${Math.round(it.progress * 100)}%` }}
                                />
                              ) : null}
                            </button>
                            {(it.milestones || []).map((m) => {
                              const milestoneTriggerProps =
                                m.richTooltip && tooltip
                                  ? tooltip.getTriggerProps({
                                      surfaceId: m.richTooltip.surfaceId,
                                      payload: m.richTooltip,
                                      testIdPrefix: 'rich-tooltip',
                                      ariaLabel: `${m.label} milestone details`,
                                    })
                                  : {};
                              if (m.richTooltip) {
                                return (
                                  <button
                                    key={m.id}
                                    type="button"
                                    className={`gantt-milestone gantt-milestone-btn tone-${m.tone || 'amber'} rich-tooltip-svg-trigger`}
                                    style={{ left: `${pct(m.date)}%` }}
                                    data-testid={`gantt-milestone-${m.id}`}
                                    {...milestoneTriggerProps}
                                  >
                                    <span className="diamond-marker" />
                                  </button>
                                );
                              }
                              return (
                                <span
                                  key={m.id}
                                  className={`gantt-milestone tone-${m.tone || 'amber'}`}
                                  style={{ left: `${pct(m.date)}%` }}
                                >
                                  <span className="diamond-marker" />
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                    {laneDependencies.length ? (
                      <div className="gantt-dependency-overlay">
                        <svg
                          className="gantt-dependency-svg"
                          viewBox={`0 0 100 ${dependencyRowsHeight}`}
                          preserveAspectRatio="none"
                          aria-hidden="true"
                        >
                          {laneDependencies.map((dependency) => {
                            const predecessorIndex = itemPositions.findIndex(
                              ({ item }) => item.id === dependency.predecessorId,
                            );
                            const successorIndex = itemPositions.findIndex(
                              ({ item }) => item.id === dependency.successorId,
                            );
                            if (predecessorIndex < 0 || successorIndex < 0) return null;
                            const predecessor = itemPositions[predecessorIndex];
                            const successor = itemPositions[successorIndex];
                            const startX = predecessor.position.left + predecessor.position.width;
                            const endX = successor.position.left;
                            const elbowX = Math.max(
                              startX + 2,
                              Math.min(endX - 2, startX + Math.max(4, (endX - startX) / 2)),
                            );
                            const startY = yForIndex(predecessorIndex);
                            const endY = yForIndex(successorIndex);
                            return (
                              <path
                                key={dependency.id}
                                className={`gantt-dependency-path${dependency.criticalPathFlag ? ' is-critical' : ''}${selectedDependencyId === dependency.id ? ' is-selected' : ''}`}
                                data-testid={`gantt-dependency-${dependency.id}`}
                                d={`M ${startX} ${startY} L ${elbowX} ${startY} L ${elbowX} ${endY} L ${endX} ${endY}`}
                              />
                            );
                          })}
                        </svg>
                        {laneDependencies.map((dependency) => {
                          const predecessorIndex = itemPositions.findIndex(
                            ({ item }) => item.id === dependency.predecessorId,
                          );
                          const successorIndex = itemPositions.findIndex(
                            ({ item }) => item.id === dependency.successorId,
                          );
                          if (predecessorIndex < 0 || successorIndex < 0) return null;
                          const predecessor = itemPositions[predecessorIndex];
                          const successor = itemPositions[successorIndex];
                          const startX = predecessor.position.left + predecessor.position.width;
                          const endX = successor.position.left;
                          const buttonX = Math.max(
                            2,
                            Math.min(98, startX + Math.max(4, (endX - startX) / 2)),
                          );
                          const buttonY =
                            (yForIndex(predecessorIndex) + yForIndex(successorIndex)) / 2;
                          return (
                            <button
                              key={`${dependency.id}-hitbox`}
                              type="button"
                              className={`gantt-dependency-hitbox${dependency.criticalPathFlag ? ' is-critical' : ''}${selectedDependencyId === dependency.id ? ' is-selected' : ''}`}
                              style={{ left: `calc(${buttonX}% - 13px)`, top: `${buttonY - 13}px` }}
                              aria-label={`Dependency ${dependency.id}: ${dependency.predecessorTitle || dependency.predecessorId} to ${dependency.successorTitle || dependency.successorId}`}
                              onClick={() => onSelectDependency && onSelectDependency(dependency)}
                            >
                              <span aria-hidden="true">↘</span>
                            </button>
                          );
                        })}
                      </div>
                    ) : null}
                  </>
                );
              })()}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function monthMarkers(minTs, maxTs) {
  const out = [];
  let start = monthStart(minTs);
  while (start <= maxTs) {
    out.push({
      ts: start,
      iso: toIsoDate(start),
      label: new Date(start).toLocaleString('en-US', {
        month: 'short',
        year: '2-digit',
        timeZone: 'UTC',
      }),
    });
    const d = new Date(start);
    start = Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1);
  }
  return out;
}

function parseDateOnly(value) {
  if (!value) return Number.NaN;
  if (value instanceof Date)
    return Date.UTC(value.getFullYear(), value.getMonth(), value.getDate());
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const ts = new Date(value).getTime();
  return Number.isNaN(ts) ? Number.NaN : ts;
}

function monthStart(ts) {
  const d = new Date(ts);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
}

function nextMonthStart(ts) {
  const start = monthStart(ts);
  const d = new Date(start);
  const next = Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1);
  return next <= start ? start + DAY_MS : next;
}

function toIsoDate(ts) {
  return new Date(ts).toISOString().slice(0, 10);
}
